import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { RadiusInsight } from "./match-result-derive";

const SIZE = 260;
const CENTER = SIZE / 2;
// Pixel-Radius des Max-Rings; Punkte jenseits des Radius werden knapp ausserhalb geklemmt.
const MAX_RING_PX = 100;
const OUTSIDE_CLAMP = 1.16;
// Untergrenze, damit nahe Kandidaten nicht den Ausgangspunkt ueberdecken.
const MIN_FACTOR = 0.16;
const DOT_R = 4;
const DOT_R_HOVER = 5.5;
// Goldener Winkel verteilt die Punkte gleichmaessig. Der Winkel ist schematisch, es gibt
// clientseitig keine Koordinaten, nur die Distanz.
const GOLDEN_ANGLE_DEG = 137.508;

// Wurzel-Skala statt linear: bei grossem Radius (z.B. 1000 km) klumpen sonst alle nahen
// Kandidaten unlesbar im Zentrum. sqrt spreizt den Innenbereich, die Ringe tragen weiter
// echte km-Beschriftungen an ihrer gestauchten Position.
function distanceFactor(distanceKm: number, radiusKm: number): number {
  return Math.min(Math.max(Math.sqrt(distanceKm / radiusKm), MIN_FACTOR), OUTSIDE_CLAMP);
}

export function RadiusInsightMap({
  insight,
  onSelectPerson,
}: {
  insight: RadiusInsight;
  onSelectPerson?: (personId: string) => void;
}) {
  const intl = useIntl();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const insideCount = insight.points.filter((point) => point.inside).length;
  const outsideCount = insight.points.length - insideCount;
  const markers = [
    ...insight.points.map((point, index) => {
      const angle = ((-90 + index * GOLDEN_ANGLE_DEG) * Math.PI) / 180;
      const radiusPx = distanceFactor(point.distanceKm, insight.radiusKm) * MAX_RING_PX;
      return {
        personId: point.personId,
        kind: point.inside ? ("inside" as const) : ("outside" as const),
        x: CENTER + radiusPx * Math.cos(angle),
        y: CENTER + radiusPx * Math.sin(angle),
        label: `${point.personName} · ${intl.formatNumber(point.distanceKm)} km`,
      };
    }),
    // Ohne Distanz gibt es keine ehrliche Position im Distanzraum: Wartebereich in der
    // freien Ecke unten links, ausserhalb der Ringe, gleiche Hover/Klick-Mechanik.
    ...insight.missing.map((person, index) => ({
      personId: person.personId,
      kind: "missing" as const,
      x: 16 + (index % 6) * 20,
      y: SIZE - 14 - Math.floor(index / 6) * 20,
      label: person.personName,
    })),
  ];
  const hovered = hoveredIndex === null ? null : (markers[hoveredIndex] ?? null);
  return (
    <div className="min-w-0" data-testid="ranking-insight-radius">
      {/* Eine Kopfzeile wie die Nachbarspalten (Radius-Info integriert): so greift die Karte
          die Linie der ersten Inhaltszeile der anderen Spalten auf und startet hoeher. */}
      <div className="text-smycles-micro tracking-[0.2em] text-ink-muted">
        <FormattedMessage id="match.result.insights.radius" defaultMessage="Umkreis" />
        {" · "}
        <FormattedMessage
          id="match.result.insights.radiusTitle"
          defaultMessage="{radius} km um {city}"
          values={{ radius: insight.radiusKm, city: insight.city }}
        />
      </div>
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="mx-auto mt-2 w-full max-w-[26rem]"
        role="img"
        aria-label={intl.formatMessage(
          {
            id: "match.result.insights.radiusMapAria",
            defaultMessage: "Umkreis-Karte: Kandidaten nach Distanz zu {city}",
          },
          { city: insight.city },
        )}
      >
        {[0.25, 0.5, 0.75].map((fraction) => (
          <g key={fraction}>
            <circle
              cx={CENTER}
              cy={CENTER}
              r={Math.sqrt(fraction) * MAX_RING_PX}
              fill="none"
              strokeWidth="1"
              className="stroke-ink/10"
            />
            <text
              x={CENTER}
              y={CENTER - Math.sqrt(fraction) * MAX_RING_PX - 3}
              textAnchor="middle"
              className="fill-ink-muted text-smycles-micro tabular-nums"
            >
              {Math.round(insight.radiusKm * fraction)}
            </text>
          </g>
        ))}
        <circle
          cx={CENTER}
          cy={CENTER}
          r={MAX_RING_PX}
          strokeWidth="1.5"
          strokeDasharray="4 4"
          className="fill-accent/5 stroke-accent-decoration/60"
        />
        <text
          x={CENTER}
          y={CENTER - MAX_RING_PX - 4}
          textAnchor="middle"
          className="fill-ink-accent text-smycles-micro font-semibold tabular-nums"
        >
          {intl.formatNumber(insight.radiusKm)} km
        </text>
        {/* Ausgangspunkt als Map-Pin, Spitze exakt auf dem Zentrum: die einzige Nicht-Kreisform
            hebt sich von allen Bubbles ab. Ein weisser Punkt mit Rahmen waere mit der
            "ausserhalb"-Optik verwechselbar. Bubbles rendern danach, liegen also drueber. */}
        <g transform={`translate(${CENTER} ${CENTER})`} pointerEvents="none">
          <path
            d="M0 0 C -3.6 -4.8, -5 -6.6, -5 -9 A 5 5 0 1 1 5 -9 C 5 -6.6, 3.6 -4.8, 0 0 Z"
            className="fill-ink"
          />
          <circle cx="0" cy="-9" r="2" className="fill-surface" />
        </g>
        {markers.map((marker, index) => (
          <circle
            key={marker.personId}
            cx={marker.x}
            cy={marker.y}
            r={hoveredIndex === index ? DOT_R_HOVER : DOT_R}
            strokeWidth="1.8"
            role="button"
            tabIndex={0}
            aria-label={marker.label}
            className={`cursor-pointer outline-none transition-[r] focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink-ui focus-visible:outline-offset-2 ${
              marker.kind === "inside"
                ? "fill-accent-decoration"
                : marker.kind === "outside"
                  ? "fill-transparent stroke-accent-decoration"
                  : "fill-ink-ui"
            }`}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex((prev) => (prev === index ? null : prev))}
            onFocus={() => setHoveredIndex(index)}
            onBlur={() => setHoveredIndex((prev) => (prev === index ? null : prev))}
            onClick={() => onSelectPerson?.(marker.personId)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSelectPerson?.(marker.personId);
              }
            }}
          />
        ))}
        {/* Hover-Label zuletzt gerendert, damit es immer ueber Ringen und Punkten liegt. */}
        {hovered ? (
          <text
            x={hovered.x > CENTER + 30 ? hovered.x - 9 : hovered.x + 9}
            y={hovered.y + 3.5}
            textAnchor={hovered.x > CENTER + 30 ? "end" : "start"}
            paintOrder="stroke"
            strokeWidth="3"
            pointerEvents="none"
            className="fill-ink stroke-surface text-smycles-micro font-medium"
          >
            {hovered.label}
          </text>
        ) : null}
      </svg>
      <div className="mt-1 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-smycles-label tabular-nums text-ink">
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden className="h-2 w-2 rounded-full bg-accent" />
          <FormattedMessage
            id="match.result.insights.radiusInside"
            defaultMessage="{count} innerhalb"
            values={{ count: insideCount }}
          />
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden className="h-2 w-2 rounded-full border-[1.5px] border-accent-decoration" />
          <FormattedMessage
            id="match.result.insights.radiusOutside"
            defaultMessage="{count} außerhalb"
            values={{ count: outsideCount }}
          />
        </span>
        {insight.missing.length > 0 ? (
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden className="h-2 w-2 rounded-full bg-ink/25" />
            <FormattedMessage
              id="match.result.insights.radiusMissing"
              defaultMessage="{count} ohne Distanz"
              values={{ count: insight.missing.length }}
            />
          </span>
        ) : null}
      </div>
      <div className="mt-1 text-center text-smycles-micro text-ink-muted">
        <FormattedMessage
          id="match.result.insights.radiusHint"
          defaultMessage="Winkel schematisch, Distanzskala gestaucht"
        />
      </div>
    </div>
  );
}
